<div class="sku-create-container">
  <!-- Toast Messages -->
  <p-toast></p-toast>

  <p-confirmDialog
    key="skuConfirm"
    [styleClass]="
      'max-w-md w-[28rem] rounded-xl shadow-2xl border-0 overflow-hidden'
    "
    [maskStyleClass]="'backdrop-blur-sm bg-black/40'"
    appendTo="body"
  >
    <ng-template pTemplate="message" let-message>
      <div class="px-3 pl-0 py-2">
        <p class="text-gray-800 dark:text-gray-200 leading-6">
          {{ message?.message | translate }}
        </p>
      </div>
    </ng-template>
  </p-confirmDialog>

  <!-- Sticky Header -->
  <div class="sticky-header">
    <div class="flex justify-between items-center mb-5">
      <div>
        <h2 class="text-2xl font-semibold text-gray-800 mb-2">
          {{
            isPackageMode()
              ? ("sku_management.create.package_title" | translate)
              : ("sku_management.create.title" | translate)
          }}
        </h2>
      </div>
      <div class="flex gap-3">
        <app-button
          [label]="'common.actions.cancel' | translate"
          type="secondary"
          [outlined]="true"
          (emitOnClick)="onCancel()"
        ></app-button>
        <app-button
          [label]="'sku_management.create.save_as_draft' | translate"
          type="info"
          [outlined]="true"
          (emitOnClick)="onSaveSkus(true)"
          [disabled]="
            manuallyGeneratedSkus().length === 0 ||
            hasAnyInvalidSkuCodes() ||
            saving()
          "
          [loading]="saving()"
        ></app-button>
        <app-button
          [label]="'sku_management.create.save_and_submit' | translate"
          type="info"
          (emitOnClick)="onSaveSkus(false)"
          [disabled]="
            manuallyGeneratedSkus().length === 0 ||
            hasAnyInvalidSkuCodes() ||
            saving()
          "
          [loading]="saving()"
        ></app-button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="sku-create-content">
    <!-- Product Selection Section -->
    <div class="mb-4">
      <p-card styleClass="border border-gray-200">
        <ng-template pTemplate="header">
          <div class="flex items-center justify-between px-4 py-3 bg-gray-50">
            <h3 class="text-base font-medium text-gray-800">
              {{ "sku_management.create.product_selection" | translate }}
            </h3>
            <app-button
              [label]="'sku_management.create.select_product' | translate"
              type="info"
              (emitOnClick)="onSelectProduct()"
            ></app-button>
          </div>
        </ng-template>
        <ng-template pTemplate="content">
          @if (selectedProduct()) {
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span class="text-sm font-medium text-gray-600">
                  {{ "sku_management.create.product_code" | translate }}:
                </span>
                <span class="text-gray-800 ml-2">{{
                  selectedProduct()?.spuCode
                }}</span>
              </div>
              <div>
                <span class="text-sm font-medium text-gray-600">
                  {{ "sku_management.create.product_name" | translate }}:
                </span>
                <span class="text-gray-800 ml-2">{{
                  selectedProduct()?.spuName
                }}</span>
              </div>
              <div>
                <span class="text-sm font-medium text-gray-600">
                  {{ "sku_management.create.category" | translate }}:
                </span>
                <span class="text-gray-800 ml-2">{{
                  selectedProduct()?.categoryName
                }}</span>
              </div>
              @if (!isPackageMode()) {
                <div>
                  <span class="text-sm font-medium text-gray-600">
                    {{ "sku_management.create.brand" | translate }}:
                  </span>
                  <span class="text-gray-800 ml-2">{{
                    selectedProduct()?.brandName
                  }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="text-center py-8">
              <p class="text-gray-500 mb-2">
                {{ "sku_management.create.no_product_selected" | translate }}
              </p>
            </div>
          }
        </ng-template>
      </p-card>
    </div>

    <!-- API Generated SKUs Section (Read-only) -->
    @if (
      showApiSkusSection() &&
      apiGeneratedSkus().length > 0 &&
      showExistingSkus()
    ) {
      <div class="mb-4">
        <p-card styleClass="border border-gray-200 overflow-hidden">
          <ng-template pTemplate="header">
            <div class="flex items-center justify-between px-4 py-3 bg-gray-50">
              <div class="flex items-center gap-4">
                <div class="flex items-center gap-2">
                  <h3 class="text-base font-medium text-gray-800">
                    {{ "sku_management.create.existing_skus" | translate }}
                  </h3>
                  <span
                    class="material-symbols-outlined text-gray-400 hover:text-gray-600 cursor-help text-xs"
                    [pTooltip]="
                      'sku_management.create.existing_skus_tooltip' | translate
                    "
                    tooltipPosition="top"
                  >
                    help
                  </span>
                </div>
                <div class="flex items-center gap-4 text-xs text-gray-600">
                  @if (loading()) {
                    <span
                      class="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-medium"
                    >
                      {{ "sku_management.create.loading_skus" | translate }}
                    </span>
                  } @else {
                    <span
                      class="bg-green-100 text-green-800 px-3 py-1 ml-4 rounded-full font-medium"
                    >
                      {{ apiGeneratedSkus().length }}
                      {{ "sku_management.create.existing_skus" | translate }}
                    </span>
                  }
                </div>
              </div>
              <div class="flex gap-2">
                <app-button
                  [label]="'common.actions.hide' | translate"
                  type="info"
                  [outlined]="true"
                  (emitOnClick)="toggleExistingSkus()"
                ></app-button>
                @if (!loading() && apiGeneratedSkus().length > 0) {
                  <app-button
                    [label]="'common.actions.refresh' | translate"
                    type="secondary"
                    [outlined]="true"
                    (emitOnClick)="refreshGeneratedSkus()"
                  ></app-button>
                }
              </div>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <!-- API SKUs Table -->
            <p-table
              [value]="apiGeneratedSkus()"
              [tableStyle]="{ 'min-width': '50rem' }"
              styleClass="w-full"
              [loading]="loading()"
              [showGridlines]="true"
              [scrollable]="true"
              scrollHeight="300px"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th
                    scope="col"
                    class="bg-blue-50 font-semibold text-center text-xs w-10 p-3"
                  >
                    {{
                      "sku_management.create.existing_table.serial_number"
                        | translate
                    }}
                  </th>
                  <th
                    scope="col"
                    class="bg-blue-50 font-semibold text-center text-sm p-3 w-48"
                  >
                    {{
                      "sku_management.create.existing_table.sku_name"
                        | translate
                    }}
                  </th>
                  <th
                    scope="col"
                    class="bg-blue-50 font-semibold text-center text-sm p-3 w-40"
                  >
                    <div class="flex items-center justify-center gap-1">
                      {{
                        "sku_management.create.existing_table.sku_code"
                          | translate
                      }}
                      <span
                        class="material-symbols-outlined text-gray-400 hover:text-blue-500 cursor-help text-xs"
                        [pTooltip]="
                          'sku_management.create.existing_table.sku_code_tooltip'
                            | translate
                        "
                        tooltipPosition="top"
                        >info</span
                      >
                    </div>
                  </th>
                  <th
                    scope="col"
                    class="bg-blue-50 font-semibold text-center text-sm p-3 w-32"
                  >
                    {{
                      "sku_management.create.existing_table.status" | translate
                    }}
                  </th>
                  <th
                    scope="col"
                    class="bg-blue-50 font-semibold text-center text-sm p-3 w-32"
                  >
                    {{
                      "sku_management.create.existing_table.attributes"
                        | translate
                    }}
                  </th>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="5" class="text-center py-8">
                    @if (loading()) {
                      <div class="flex flex-col items-center gap-2">
                        <p class="text-gray-500">
                          {{
                            "sku_management.create.existing_table.loading_message"
                              | translate
                          }}
                        </p>
                      </div>
                    } @else {
                      <div class="flex flex-col items-center gap-2">
                        <p class="text-gray-500">
                          No existing SKUs found for this product
                        </p>
                      </div>
                    }
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-rowData let-rowIndex="rowIndex">
                <tr class="hover:bg-gray-50 transition-colors duration-150">
                  <td
                    class="text-center text-xs p-3 text-gray-600 font-medium w-10"
                  >
                    {{ rowIndex + 1 }}
                  </td>
                  <td class="p-3">
                    <p class="text-gray-800 text-xs">
                      {{ rowData.skuName || "-" }}
                    </p>
                  </td>
                  <td class="p-3">
                    <p class="text-gray-800 text-xs font-mono">
                      {{ rowData.skuCode || "-" }}
                    </p>
                  </td>
                  <td class="p-3">
                    <div class="flex justify-center">
                      @if (rowData.status !== undefined) {
                        <p-tag
                          [value]="getSkuStatusText(rowData.status)"
                          [severity]="getSkuStatusSeverity(rowData.status)"
                          styleClass="status-tag"
                          [ngStyle]="getSkuStatusStyle(rowData.status)"
                          pTooltip="{{ getSkuStatusText(rowData.status) }}"
                          tooltipPosition="top"
                          tooltipStyleClass="custom-tooltip"
                          [showDelay]="300"
                          [hideDelay]="0"
                        ></p-tag>
                      }
                    </div>
                  </td>
                  <td class="p-3">
                    <div class="flex flex-wrap gap-1">
                      @for (
                        attr of getDisplayAttributes(rowData);
                        track attr.key + attr.value
                      ) {
                        <span
                          class="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          {{ attr.key }}: {{ attr.value }}
                        </span>
                      } @empty {
                        <span class="text-xs text-gray-400">
                          {{
                            "sku_management.create.no_attributes" | translate
                          }}
                        </span>
                      }
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </ng-template>
        </p-card>
      </div>
    }

    <!-- SKU Attributes Section -->
    @if (selectedProduct() && !isPackageMode()) {
      <div class="mb-4">
        <p-card styleClass="shadow-sm border border-gray-200">
          <ng-template pTemplate="header">
            <div class="flex items-center justify-between px-4 py-3 bg-gray-50">
              <h3 class="text-base font-medium text-gray-800">
                {{ "sku_management.create.sku_attributes" | translate }}
              </h3>
              <div class="flex gap-2">
                @if (
                  showApiSkusSection() &&
                  apiGeneratedSkus().length > 0 &&
                  !showExistingSkus()
                ) {
                  <app-button
                    [label]="
                      'sku_management.create.show_existing_skus' | translate
                    "
                    type="info"
                    [outlined]="true"
                    (emitOnClick)="toggleExistingSkus()"
                  ></app-button>
                }
                <app-button
                  [label]="'sku_management.create.generate_skus' | translate"
                  type="success"
                  (emitOnClick)="onGenerateSkus()"
                  [disabled]="!selectedProduct()"
                ></app-button>
              </div>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            @for (
              group of getSkuGeneratingAttributeGroups(selectedProduct());
              track group.sourceType
            ) {
              <div>
                <h4
                  class="text-md text-sm font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2"
                >
                  {{ group.label | translate }}
                </h4>
                @for (attribute of group.attributes; track attribute.id) {
                  <div class="mb-6 ml-4">
                    <label
                      [for]="'sku-attr-' + attribute.code"
                      class="block text-xs font-medium text-gray-700 mb-2"
                    >
                      {{ attribute.name }}
                      <span class="text-red-500">*</span>
                    </label>

                    @if (
                      attribute.submissionType === SUBMISSIONS_TYPES.CHECKBOX
                    ) {
                      <!-- Checkbox/Multi-select -->
                      <div class="flex flex-wrap gap-3">
                        @for (
                          value of attribute.attributeValueList;
                          track value.value
                        ) {
                          <div class="flex items-center radio-option">
                            <p-checkbox
                              [binary]="true"
                              [inputId]="
                                'attr-' + attribute.code + '-' + value.value
                              "
                              [ngModel]="
                                isAttributeSelected(
                                  attribute.code,
                                  value.value
                                )
                              "
                              (onChange)="
                                onAttributeValueChange(
                                  attribute.code,
                                  value.value,
                                  true
                                )
                              "
                              styleClass="custom-checkbox"
                            />
                            <label
                              [for]="
                                'attr-' + attribute.code + '-' + value.value
                              "
                              class="ml-2 text-xs text-gray-600 cursor-pointer hover:text-gray-800 transition-colors duration-200"
                            >
                              {{ value.value }}
                            </label>
                          </div>
                        }
                      </div>
                    } @else if (
                      attribute.submissionType ===
                      SUBMISSIONS_TYPES.RADIO_BUTTON
                    ) {
                      <!-- Radio Button/Single select -->
                      <div class="flex flex-wrap gap-3">
                        @for (
                          value of attribute.attributeValueList;
                          track value.value
                        ) {
                          <div class="flex items-center radio-option">
                            <p-radiobutton
                              [value]="value.value"
                              [inputId]="
                                'attr-' + attribute.code + '-' + value.value
                              "
                              [name]="attribute.code"
                              [(ngModel)]="
                                radioButtonValues()[attribute.code]
                              "
                              (onChange)="
                                onRadioButtonChange(
                                  attribute.code,
                                  value.value
                                )
                              "
                              styleClass="custom-radio"
                            />
                            <label
                              [for]="
                                'attr-' + attribute.code + '-' + value.value
                              "
                              class="ml-2 text-xs text-gray-600 cursor-pointer hover:text-gray-800 transition-colors duration-200"
                            >
                              {{ value.value }}
                            </label>
                          </div>
                        }
                      </div>
                    } @else {
                      <!-- Dropdown/Single select -->
                      <p-select
                        [inputId]="'sku-attr-' + attribute.code"
                        [options]="getAttributeOptions(attribute)"
                        optionLabel="label"
                        optionValue="value"
                        [placeholder]="
                          ('sku_management.create.select_attribute'
                            | translate) +
                          ': ' +
                          attribute.name
                        "
                        [formControl]="
                          selectedProductAttributes()[attribute.code]
                        "
                        (onChange)="
                          onAttributeChange(attribute.code, $event.value)
                        "
                        styleClass="w-full"
                      />
                    }
                  </div>
                }
              </div>
            }
          </ng-template>
        </p-card>
      </div>
    }

    <!-- Manually Generated SKUs Section (Editable) -->
    @if (showManualSkusSection() && manuallyGeneratedSkus().length > 0) {
      <div class="mb-4">
        <p-card styleClass="border border-gray-200 overflow-hidden">
          <ng-template pTemplate="header">
            <div class="flex items-center justify-between p-4 bg-gray-50">
              <div class="flex items-center gap-4">
                <div class="flex items-center gap-2">
                  <h3 class="text-base font-medium text-gray-800">
                    {{ "sku_management.create.new_skus" | translate }}
                  </h3>
                  <span
                    class="material-symbols-outlined text-gray-400 hover:text-gray-600 cursor-help text-xs"
                    [pTooltip]="
                      'sku_management.create.new_skus_tooltip' | translate
                    "
                    tooltipPosition="top"
                  >
                    help
                  </span>
                </div>
                <div class="flex items-center gap-4 text-xs text-gray-600 ml-4">
                  <span
                    class="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium"
                  >
                    {{ manuallyGeneratedSkus().length }}
                    {{ "sku_management.create.new_skus" | translate }}
                  </span>
                  @if (selectedSkus().length > 0) {
                    <span
                      class="bg-orange-100 text-orange-800 px-3 py-1 rounded-full font-medium"
                    >
                      {{ selectedSkus().length }}
                      {{ "sku_management.create.selected" | translate }}
                    </span>
                  }
                </div>
              </div>
              <div class="flex gap-2">
                @if (isPackageMode()) {
                  <app-button
                    [label]="
                      'sku_management.actions.add_package_sku' | translate
                    "
                    type="success"
                    size="small"
                    (emitOnClick)="onGenerateSkus()"
                  ></app-button>
                }
                @if (!allRowsExpanded()) {
                  <app-button
                    [label]="'sku_management.create.expand_all' | translate"
                    type="info"
                    [outlined]="true"
                    (emitOnClick)="expandAllRows()"
                  ></app-button>
                } @else {
                  <app-button
                    [label]="'sku_management.create.collapse_all' | translate"
                    type="info"
                    size="small"
                    [outlined]="true"
                    (emitOnClick)="collapseAllRows()"
                  ></app-button>
                }
                <app-button
                  [label]="'sku_management.create.delete_selected' | translate"
                  type="danger"
                  size="small"
                  [outlined]="true"
                  (emitOnClick)="onDeleteSelectedSkus()"
                  [disabled]="selectedSkus().length === 0"
                ></app-button>
              </div>
            </div>
          </ng-template>
          <ng-template pTemplate="content">
            <!-- Manual SKUs Table -->
            <p-table
              [value]="manuallyGeneratedSkus()"
              [tableStyle]="{ 'min-width': '50rem' }"
              [styleClass]="
                isPackageMode()
                  ? 'w-full new-sku-table package-new-sku-table'
                  : 'w-full new-sku-table'
              "
              [showGridlines]="true"
              [scrollable]="!isPackageMode()"
              [scrollHeight]="isPackageMode() ? undefined : '400px'"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th
                    scope="col"
                    class="new-sku-header-cell bg-white text-center w-10 p-3"
                  >
                    <p-checkbox
                      [binary]="true"
                      (onChange)="onSelectAll($event)"
                      [ngModel]="isAllSelected()"
                    ></p-checkbox>
                  </th>
                  <th
                    scope="col"
                    class="new-sku-header-cell bg-white text-center w-10 p-3"
                  >
                    {{
                      "sku_management.create.table.serial_number" | translate
                    }}
                  </th>
                  <th
                    scope="col"
                    class="new-sku-header-cell bg-white text-center p-3 w-48"
                  >
                    <div class="flex items-center justify-center">
                      {{ "sku_management.create.table.sku_name" | translate }}
                      <span class="text-red-500">*</span>
                      <span
                        class="material-symbols-outlined ml-1 text-gray-400 hover:text-blue-500 cursor-help text-xs"
                        [pTooltip]="
                          'sku_management.messages.sku_name_no_specs_hint'
                            | translate
                        "
                        tooltipPosition="top"
                        >info</span
                      >
                    </div>
                  </th>
                  <th
                    scope="col"
                    class="new-sku-header-cell bg-white text-center p-3 w-40"
                  >
                    <div class="flex items-center justify-center">
                      {{ "sku_management.create.table.sku_code" | translate }}
                      <span class="text-red-500">*</span>
                      <span
                        class="material-symbols-outlined ml-1 text-gray-400 hover:text-blue-500 cursor-help text-xs"
                        [pTooltip]="
                          'sku_management.create.table.sku_code_tooltip'
                            | translate
                        "
                        tooltipPosition="top"
                        >info</span
                      >
                    </div>
                  </th>
                  <th
                    scope="col"
                    class="new-sku-header-cell bg-white text-center p-3 w-32"
                  >
                    {{ "sku_management.create.table.attributes" | translate }}
                  </th>
                  <th
                    scope="col"
                    class="new-sku-header-cell bg-white text-center p-3 w-32"
                  >
                    {{ "sku_management.create.table.actions" | translate }}
                  </th>
                </tr>
              </ng-template>

              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="6" class="text-center py-8">
                    <div class="flex flex-col items-center gap-2">
                      <p class="text-gray-500">
                        {{
                          "sku_management.create.no_manual_skus_message"
                            | translate
                        }}
                      </p>
                    </div>
                  </td>
                </tr>
              </ng-template>

              <ng-template pTemplate="body" let-rowData let-rowIndex="rowIndex">
                <tr class="hover:bg-gray-50 transition-colors duration-150">
                  <td class="text-center p-3">
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="isRowSelected(rowData)"
                      (onChange)="onRowSelectionChange($event, rowData)"
                    ></p-checkbox>
                  </td>
                  <td
                    class="text-center text-xs p-3 text-gray-600 font-medium w-10"
                  >
                    {{ rowIndex + 1 }}
                  </td>
                  <td class="p-3">
                    <div class="space-y-1">
                      <input
                        pInputText
                        [value]="rowData.skuName"
                        (input)="onInputChange($event, rowData, 'skuName')"
                        (blur)="onSkuNameBlur($event, rowData)"
                        [class]="getSkuNameInputClass(rowData.skuName)"
                        [placeholder]="
                          'sku_management.create.enter_sku_name' | translate
                        "
                      />
                      @if (
                        rowData.skuName && isSkuNameInvalid(rowData.skuName)
                      ) {
                        <div class="text-xs text-red-500">
                          {{ getSkuNameValidationMessage(rowData.skuName) }}
                        </div>
                      }
                    </div>
                  </td>
                  <td class="p-3">
                    <div class="space-y-1">
                      <input
                        pInputText
                        [value]="rowData.skuCode"
                        (input)="onInputChange($event, rowData, 'skuCode')"
                        (blur)="onSkuCodeBlur($event, rowData)"
                        (paste)="onSkuCodePaste($event, rowData)"
                        [class]="getSkuCodeInputClass(rowData.skuCode)"
                        [placeholder]="
                          'sku_management.create.enter_sku_code' | translate
                        "
                      />
                      @if (
                        rowData.skuCode && hasSpecialCharacters(rowData.skuCode)
                      ) {
                        <div class="text-xs text-red-500">
                          {{ getSkuCodeValidationMessage(rowData.skuCode) }}
                        </div>
                        <div class="mt-1">
                          @for (
                            suggestion of getSkuCodeSuggestions(
                              rowData.skuCode
                            );
                            track suggestion
                          ) {
                            <div class="text-xs text-blue-600 italic">
                              💡 {{ suggestion }}
                            </div>
                          }
                        </div>
                      }
                    </div>
                  </td>
                  <td class="p-3">
                    <div class="flex flex-wrap gap-1">
                      @for (
                        attr of getDisplayAttributes(rowData);
                        track attr.key + attr.value
                      ) {
                        <span
                          class="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          {{ attr.key }}: {{ attr.value }}
                        </span>
                      } @empty {
                        <span class="text-xs text-gray-400">
                          {{
                            "sku_management.create.no_attributes" | translate
                          }}
                        </span>
                      }
                    </div>
                  </td>
                  <td class="text-center p-3">
                    <app-button
                      [label]="
                        isRowExpanded(rowData)
                          ? ('sku_management.create.collapse' | translate)
                          : ('sku_management.create.expand' | translate)
                      "
                      type="info"
                      [outlined]="true"
                      (emitOnClick)="toggleRowExpansion(rowData)"
                    ></app-button>
                  </td>
                </tr>

                <!-- Expanded Row for Detailed Editing -->
                @if (isRowExpanded(rowData)) {
                  <tr class="bg-gray-50">
                    <td colspan="6" class="p-0">
                      <div class="p-6 border-t border-gray-200">
                        <div class="w-full">
                          <div class="expanded-sku-sections">
                            <div class="tax-configuration-section">
                              <div class="flex items-center gap-2 mb-1.5">
                                <h4 class="font-semibold text-gray-800 text-sm">
                                  {{ "goods.tax.section_title" | translate }}
                                </h4>
                                <button
                                  type="button"
                                  class="apply-to-all-btn"
                                  (click)="applyTaxToAll(rowData)"
                                  [disabled]="
                                    manuallyGeneratedSkus().length <= 1
                                  "
                                  [pTooltip]="
                                    'goods.tax.apply_to_all_tooltip'
                                      | translate
                                  "
                                  tooltipPosition="top"
                                >
                                  <span class="material-symbols-outlined"
                                    >content_copy</span
                                  >
                                </button>
                              </div>
                              <app-product-tax-control
                                [value]="
                                  rowData.taxValue ||
                                  {
                                    mode: 'INHERIT',
                                    taxId: null,
                                    isPriceIncludeVAT: null
                                  }
                                "
                                [inheritedValue]="rowData.inheritedTaxValue"
                                [taxOptions]="taxOptions()"
                                [compact]="true"
                                [idPrefix]="'sku-create-tax-' + rowIndex"
                                (valueChange)="
                                  onSkuTaxValueChange(rowData, $event)
                                "
                              ></app-product-tax-control>
                            </div>

                            <!-- System Attributes Section -->
                            <div class="space-y-4 system-attributes-section">
                              <div class="flex items-center gap-2 mb-1.5">
                                <h4 class="font-semibold text-gray-800 text-sm">
                                  {{
                                    "sku_management.messages.system_attributes"
                                      | translate
                                  }}
                                </h4>
                                <button
                                  type="button"
                                  class="apply-to-all-btn"
                                  (click)="
                                    applyAllSystemAttributesToAll(rowData)
                                  "
                                  [disabled]="
                                    manuallyGeneratedSkus().length <= 1
                                  "
                                  [pTooltip]="
                                    'sku_management.create.apply_all_system_attrs_tooltip'
                                      | translate
                                  "
                                  tooltipPosition="top"
                                >
                                  <span class="material-symbols-outlined"
                                    >content_copy</span
                                  >
                                </button>
                              </div>
                              <div
                                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                              >
                                <div class="space-y-2">
                                  <label
                                    [for]="'weight-' + rowIndex"
                                    class="text-xs font-medium text-gray-700"
                                    >{{
                                      "sku_management.messages.weight"
                                        | translate
                                    }}</label
                                  >
                                  <div class="input-with-button-wrapper">
                                    <p-inputNumber
                                      [inputId]="'weight-' + rowIndex"
                                      [(ngModel)]="rowData.weight"
                                      [min]="0"
                                      mode="decimal"
                                      [maxFractionDigits]="dimensionFractionDigits"
                                      [useGrouping]="false"
                                      class="input-field"
                                      placeholder="0.00"
                                    />
                                    <button
                                      type="button"
                                      class="apply-to-all-btn-wrapper"
                                      (click)="
                                        applyWeightToAll(
                                          rowData.weight,
                                          rowData
                                        )
                                      "
                                      [disabled]="
                                        manuallyGeneratedSkus().length <= 1
                                      "
                                      [pTooltip]="
                                        'sku_management.create.apply_weight_tooltip'
                                          | translate
                                      "
                                      tooltipPosition="top"
                                    >
                                      <span class="material-symbols-outlined"
                                        >content_copy</span
                                      >
                                    </button>
                                  </div>
                                </div>
                                <div class="space-y-2">
                                  <label
                                    [for]="'length-' + rowIndex"
                                    class="text-xs font-medium text-gray-700"
                                    >{{
                                      "sku_management.messages.length"
                                        | translate
                                    }}</label
                                  >
                                  <div class="input-with-button-wrapper">
                                    <p-inputNumber
                                      [inputId]="'length-' + rowIndex"
                                      [(ngModel)]="rowData.length"
                                      [min]="0"
                                      mode="decimal"
                                      [maxFractionDigits]="dimensionFractionDigits"
                                      [useGrouping]="false"
                                      class="input-field"
                                      placeholder="0.00"
                                    />
                                    <button
                                      type="button"
                                      class="apply-to-all-btn-wrapper"
                                      (click)="
                                        applyLengthToAll(
                                          rowData.length,
                                          rowData
                                        )
                                      "
                                      [disabled]="
                                        manuallyGeneratedSkus().length <= 1
                                      "
                                      [pTooltip]="
                                        'sku_management.create.apply_length_tooltip'
                                          | translate
                                      "
                                      tooltipPosition="top"
                                    >
                                      <span class="material-symbols-outlined"
                                        >content_copy</span
                                      >
                                    </button>
                                  </div>
                                </div>
                                <div class="space-y-2">
                                  <label
                                    [for]="'width-' + rowIndex"
                                    class="text-xs font-medium text-gray-700"
                                    >{{
                                      "sku_management.messages.width"
                                        | translate
                                    }}</label
                                  >
                                  <div class="input-with-button-wrapper">
                                    <p-inputNumber
                                      [inputId]="'width-' + rowIndex"
                                      [(ngModel)]="rowData.width"
                                      [min]="0"
                                      mode="decimal"
                                      [maxFractionDigits]="dimensionFractionDigits"
                                      [useGrouping]="false"
                                      class="input-field"
                                      placeholder="0.00"
                                    />
                                    <button
                                      type="button"
                                      class="apply-to-all-btn-wrapper"
                                      (click)="
                                        applyWidthToAll(rowData.width, rowData)
                                      "
                                      [disabled]="
                                        manuallyGeneratedSkus().length <= 1
                                      "
                                      [pTooltip]="
                                        'sku_management.create.apply_width_tooltip'
                                          | translate
                                      "
                                      tooltipPosition="top"
                                    >
                                      <span class="material-symbols-outlined"
                                        >content_copy</span
                                      >
                                    </button>
                                  </div>
                                </div>
                                <div class="space-y-2">
                                  <label
                                    [for]="'height-' + rowIndex"
                                    class="text-xs font-medium text-gray-700"
                                    >{{
                                      "sku_management.messages.height"
                                        | translate
                                    }}</label
                                  >
                                  <div class="input-with-button-wrapper">
                                    <p-inputNumber
                                      [inputId]="'height-' + rowIndex"
                                      [(ngModel)]="rowData.height"
                                      [min]="0"
                                      mode="decimal"
                                      [maxFractionDigits]="dimensionFractionDigits"
                                      [useGrouping]="false"
                                      class="input-field"
                                      placeholder="0.00"
                                    />
                                    <button
                                      type="button"
                                      class="apply-to-all-btn-wrapper"
                                      (click)="
                                        applyHeightToAll(
                                          rowData.height,
                                          rowData
                                        )
                                      "
                                      [disabled]="
                                        manuallyGeneratedSkus().length <= 1
                                      "
                                      [pTooltip]="
                                        'sku_management.create.apply_height_tooltip'
                                          | translate
                                      "
                                      tooltipPosition="top"
                                    >
                                      <span class="material-symbols-outlined"
                                        >content_copy</span
                                      >
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            @if (isPackageMode()) {
                              <div class="space-y-4 package-components-section">
                                <div class="flex items-center justify-between">
                                  <h4 class="font-semibold text-gray-800 text-sm">
                                    {{
                                      "sku_management.create.package_components.title"
                                        | translate
                                    }}
                                  </h4>
                                  <div class="flex items-center gap-2">
                                    <app-button
                                      [label]="
                                        'sku_management.create.package_components.add_child_sku'
                                          | translate
                                      "
                                      type="info"
                                      size="small"
                                      [outlined]="true"
                                      (emitOnClick)="
                                        openPackageComponentSelector(rowData)
                                      "
                                    ></app-button>
                                    <app-button
                                      [label]="
                                        'sku_management.create.delete_selected'
                                          | translate
                                      "
                                      type="danger"
                                      size="small"
                                      [outlined]="true"
                                      [disabled]="
                                        getSelectedPackageComponentsForSku(
                                          rowData
                                        ).length === 0
                                      "
                                      (emitOnClick)="
                                        removeSelectedPackageComponents(rowData)
                                      "
                                    ></app-button>
                                  </div>
                                </div>
                                <p-table
                                  [value]="rowData.skuDetailList || []"
                                  [tableStyle]="{
                                    width: '100%',
                                    'min-width': '54rem',
                                    'table-layout': 'fixed'
                                  }"
                                  styleClass="w-full package-components-table"
                                  [showGridlines]="true"
                                >
                                  <ng-template pTemplate="header">
                                    <tr>
                                      <th class="text-center p-2 w-[4%]">
                                        <p-checkbox
                                          [binary]="true"
                                          [ngModel]="
                                            isAllPackageComponentsSelected(
                                              rowData
                                            )
                                          "
                                          (onChange)="
                                            onPackageComponentSelectAll(
                                              $event,
                                              rowData
                                            )
                                          "
                                        ></p-checkbox>
                                      </th>
                                      <th class="text-center p-2 w-[16%]">
                                        {{
                                          "sku_management.create.package_components.sku_code"
                                            | translate
                                        }}
                                      </th>
                                      <th class="text-center p-2 w-[22%]">
                                        {{
                                          "sku_management.create.package_components.sku_name"
                                            | translate
                                        }}
                                      </th>
                                      <th class="text-center p-2 w-[14%]">
                                        {{
                                          "sku_management.create.package_components.qty"
                                            | translate
                                        }}
                                      </th>
                                      <th class="text-center p-2 w-[14%]">
                                        {{
                                          "sku_management.create.package_components.action"
                                            | translate
                                        }}
                                      </th>
                                    </tr>
                                  </ng-template>
                                  <ng-template
                                    pTemplate="body"
                                    let-component
                                  >
                                    <tr>
                                      <td class="text-center p-2">
                                        <p-checkbox
                                          [binary]="true"
                                          [ngModel]="
                                            isPackageComponentSelected(
                                              rowData,
                                              component
                                            )
                                          "
                                          (onChange)="
                                            onPackageComponentRowSelectionChange(
                                              $event,
                                              rowData,
                                              component
                                            )
                                          "
                                        ></p-checkbox>
                                      </td>
                                      <td class="text-xs p-2">
                                        {{ component.skuCode }}
                                      </td>
                                      <td class="text-xs p-2">
                                        {{ component.skuName }}
                                      </td>
                                      <td class="p-2 package-component-quantity-cell">
                                        <p-inputNumber
                                          [(ngModel)]="component.num"
                                          [min]="1"
                                          [showButtons]="true"
                                          buttonLayout="horizontal"
                                          spinnerMode="horizontal"
                                          decrementButtonIcon="pi pi-minus"
                                          incrementButtonIcon="pi pi-plus"
                                          [useGrouping]="true"
                                          [minFractionDigits]="0"
                                          [maxFractionDigits]="0"
                                          [inputStyle]="{
                                            textAlign: 'center',
                                            minWidth: '3rem'
                                          }"
                                          (onInput)="
                                            onPackageComponentQuantityChange(
                                              rowData
                                            )
                                          "
                                          styleClass="w-full package-component-quantity-input"
                                        />
                                      </td>
                                      <td class="text-center p-2">
                                        <button
                                          type="button"
                                          class="action-link action-delete"
                                          (click)="
                                            removePackageComponent(
                                              rowData,
                                              component
                                            )
                                          "
                                        >
                                          {{
                                            "sku_management.create.package_components.remove"
                                              | translate
                                          }}
                                        </button>
                                      </td>
                                    </tr>
                                  </ng-template>
                                  <ng-template pTemplate="emptymessage">
                                    <tr>
                                      <td
                                        colspan="5"
                                        class="text-center text-xs text-gray-500 py-4"
                                      >
                                        {{
                                          "sku_management.create.package_components.no_child_sku_selected"
                                            | translate
                                        }}
                                      </td>
                                    </tr>
                                  </ng-template>
                                </p-table>
                              </div>
                            }

                            <!-- Barcode Table Section -->
                            <app-barcode-table
                              class="barcode-section"
                              [barcodeList]="getBarcodeListForSku(rowData)"
                              [selectedBarcodes]="
                                getSelectedBarcodesForSku(rowData)
                              "
                              [showTitle]="true"
                              [title]="'sku_management.barcode.title'"
                              (addBarcode)="onAddBarcode(rowData)"
                              (editBarcode)="onEditBarcode(rowData, $event)"
                              (deleteBarcode)="onDeleteBarcode(rowData, $event)"
                              (deleteSelectedBarcodes)="
                                onDeleteSelectedBarcodes(rowData)
                              "
                              (selectionChange)="
                                onBarcodeSelectionChange(rowData, $event)
                              "
                            ></app-barcode-table>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
              </ng-template>
            </p-table>
          </ng-template>
        </p-card>
      </div>
    }

    <!-- Product Selection Dialog -->
    <p-dialog
      [visible]="showProductSelector()"
      (visibleChange)="showProductSelector.set($event)"
      [modal]="true"
      [style]="{ width: '90vw', maxWidth: '1200px' }"
      [draggable]="false"
      [resizable]="false"
      [closable]="false"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center justify-between w-full">
          <h2 class="text-xl font-semibold text-gray-800 m-0">
            {{ "sku_management.create.select_standard_product" | translate }}
          </h2>
          <button
            type="button"
            class="custom-close-btn"
            (click)="onProductSelectionCancel()"
            [attr.aria-label]="'common.actions.close_dialog' | translate"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
      </ng-template>
      <div class="product-selection-dialog-content">
        <!-- Search and Filter Section -->
        <div class="bg-gray-50 p-4 rounded-lg flex-shrink-0">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-2">
              <label
                for="product-search-code"
                class="text-xs font-medium text-gray-700"
                >{{ "sku_management.create.product_code" | translate }}</label
              >
              <input
                pInputText
                id="product-search-code"
                [(ngModel)]="productSearchForm.productCode"
                [placeholder]="
                  'sku_management.create.enter_product_code' | translate
                "
                class="w-full"
              />
            </div>
            <div class="space-y-2">
              <label
                for="product-search-name"
                class="text-xs font-medium text-gray-700"
                >{{ "sku_management.create.product_name" | translate }}</label
              >
              <input
                pInputText
                id="product-search-name"
                [(ngModel)]="productSearchForm.productName"
                [placeholder]="
                  'sku_management.create.enter_product_name' | translate
                "
                class="w-full"
              />
            </div>
          </div>
          <div class="flex gap-2 mt-4 justify-end">
            <app-button
              [label]="'common.actions.search' | translate"
              type="info"
              size="small"
              (emitOnClick)="onProductSearch()"
            ></app-button>
            <app-button
              [label]="'common.actions.reset' | translate"
              type="secondary"
              [outlined]="true"
              size="small"
              (emitOnClick)="onProductSearchReset()"
            ></app-button>
          </div>
        </div>

        <!-- Products Table -->
        <div class="product-table-wrapper flex-1">
          <app-data-table
            [dataSource]="filteredProductOptions()"
            [columns]="productTableColumns()"
            [loading]="productTableLoading()"
            [hasCheckColumn]="true"
            [hasSerialColumn]="false"
            [hasHeaderCheckbox]="false"
            [rows]="productPageSize()"
            [first]="(productCurrentPage() - 1) * productPageSize()"
            [totalRecords]="productTotalRecords()"
            [scrollHeight]="'calc(90vh - 400px)'"
            [selection]="
              selectedProductForTable() ? [selectedProductForTable()] : []
            "
            (selectionChange)="onProductTableSelectionChange($event)"
            (pageChange)="onProductPageChange($event)"
          >
          </app-data-table>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
          <app-button
            [label]="'common.actions.cancel' | translate"
            type="secondary"
            [outlined]="true"
            size="small"
            (emitOnClick)="onProductSelectionCancel()"
            [disabled]="loading()"
          ></app-button>
          <app-button
            [label]="'common.actions.confirm' | translate"
            type="info"
            size="small"
            (emitOnClick)="onProductSelectionConfirm()"
            [disabled]="!selectedProductForTable() || loading()"
          ></app-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- SKU Selection Dialog for Bulk Apply -->
    <p-dialog
      [(visible)]="showSkuSelectionDialog"
      [modal]="true"
      [style]="{ width: '80vw', maxWidth: '1000px' }"
      [draggable]="false"
      [resizable]="false"
      [closable]="false"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center justify-between w-full">
          <h2 class="text-xl font-semibold text-gray-800 m-0">
            {{ "sku_management.create.select_skus_to_apply_value" | translate }}
          </h2>
          <button
            type="button"
            class="custom-close-btn"
            (click)="cancelSkuSelection()"
            [attr.aria-label]="'common.actions.close_dialog' | translate"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
      </ng-template>
      <div class="p-4 pl-0">
        <div class="mb-4">
          <p class="text-gray-700 mb-2">
            @if (bulkApplyValue() !== null) {
              {{
                "sku_management.messages.select_skus_modal_description"
                  | translate
              }}
              <strong>{{ bulkApplyFieldName() }}</strong>
              {{ "sku_management.messages.value" | translate }}
              <strong>{{ bulkApplyValue() }}</strong>
              {{ "sku_management.messages.to" | translate }}
            } @else {
              {{
                "sku_management.messages.select_skus_modal_description"
                  | translate
              }}
              <strong>{{ bulkApplyFieldName() }}</strong
              >:
            }
          </p>
          <p class="text-sm text-gray-500">
            {{
              "sku_management.messages.select_skus_modal_instruction"
                | translate
            }}
          </p>
        </div>

        <!-- SKU Selection Table -->
        <div class="border border-gray-200 rounded-lg overflow-hidden">
          <p-table
            [value]="availableSkusForBulkApply()"
            [tableStyle]="{ 'min-width': '50rem' }"
            styleClass="w-full"
            [showGridlines]="true"
            [scrollable]="true"
            scrollHeight="300px"
          >
            <ng-template pTemplate="header">
              <tr>
                <th
                  scope="col"
                  class="bg-gray-50 font-semibold text-center text-xs w-10 p-3"
                >
                  <p-checkbox
                    [binary]="true"
                    (onChange)="onBulkApplySelectAll($event)"
                    [ngModel]="isBulkApplyAllSelected()"
                  ></p-checkbox>
                </th>
                <th
                  scope="col"
                  class="bg-gray-50 font-semibold text-center text-xs w-10 p-3"
                >
                  {{ "sku_management.messages.table_no" | translate }}
                </th>
                <th
                  scope="col"
                  class="bg-gray-50 font-semibold text-center text-sm p-3 w-48"
                >
                  {{ "sku_management.messages.table_sku_name" | translate }}
                </th>
                <th
                  scope="col"
                  class="bg-gray-50 font-semibold text-center text-sm p-3 w-40"
                >
                  {{ "sku_management.messages.table_sku_code" | translate }}
                </th>
                <th
                  scope="col"
                  class="bg-gray-50 font-semibold text-center text-sm p-3 w-32"
                >
                  {{ "sku_management.messages.table_attributes" | translate }}
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-rowData let-rowIndex="rowIndex">
              <tr class="hover:bg-gray-50 transition-colors duration-150">
                <td class="text-center p-3">
                  <p-checkbox
                    [binary]="true"
                    [ngModel]="isBulkApplySkuSelected(rowData)"
                    (onChange)="onBulkApplySkuSelectionChange($event, rowData)"
                  ></p-checkbox>
                </td>
                <td
                  class="text-center text-xs p-3 text-gray-600 font-medium w-10"
                >
                  {{ rowIndex + 1 }}
                </td>
                <td class="p-3">
                  <span class="text-xs text-gray-700 font-medium">{{
                    rowData.skuName
                  }}</span>
                </td>
                <td class="p-3">
                  <span class="text-xs text-gray-700 font-mono">{{
                    rowData.skuCode
                  }}</span>
                </td>
                <td class="p-3">
                  <div class="flex flex-wrap gap-1">
                    @for (attr of getSkuAttributes(rowData); track attr.key) {
                      <span
                        class="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                      >
                        {{ attr.key }}: {{ attr.value }}
                      </span>
                    }
                  </div>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Selection Summary -->
        <div class="mt-4 p-3 bg-blue-50 rounded-lg">
          <p class="text-sm text-blue-800">
            {{
              "sku_management.messages.selection_summary"
                | translate
                  : {
                      selected: bulkApplySelectedSkus().length,
                      total: availableSkusForBulkApply().length,
                    }
            }}
          </p>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
          <app-button
            [label]="'common.actions.cancel' | translate"
            type="secondary"
            [outlined]="true"
            size="small"
            (emitOnClick)="cancelSkuSelection()"
          ></app-button>
          <app-button
            [label]="'common.actions.continue' | translate"
            type="primary"
            size="small"
            (emitOnClick)="proceedToBulkApplyConfirmation()"
            [disabled]="bulkApplySelectedSkus().length === 0"
          ></app-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Bulk Apply Confirmation Dialog -->
    <p-dialog
      [header]="'sku_management.create.apply_to_selected_skus' | translate"
      [(visible)]="showBulkApplyDialog"
      [modal]="true"
      [style]="{ width: '400px' }"
      [draggable]="false"
      [resizable]="false"
      [closable]="false"
    >
      <div class="p-4 pl-0">
        <p class="text-gray-700 mb-4">
          @if (bulkApplyValue() !== null) {
            {{
              "sku_management.messages.bulk_apply_confirmation_description"
                | translate
            }}
            <strong>{{ bulkApplyFieldName() }}</strong>
            {{ "sku_management.messages.value" | translate }}
            <strong>{{ bulkApplyValue() }}</strong>
            {{
              "sku_management.messages.bulk_apply_confirmation_to" | translate
            }}
            <strong>{{ bulkApplyCount() }}</strong>
            {{
              bulkApplyCount() > 1
                ? ("sku_management.messages.bulk_apply_confirmation_skus_plural"
                  | translate)
                : ("sku_management.messages.bulk_apply_confirmation_skus"
                  | translate)
            }}?
          } @else {
            {{
              "sku_management.messages.bulk_apply_confirmation_description"
                | translate
            }}
            <strong>{{ bulkApplyFieldName() }}</strong>
            {{
              "sku_management.messages.bulk_apply_confirmation_to" | translate
            }}
            <strong>{{ bulkApplyCount() }}</strong>
            {{
              bulkApplyCount() > 1
                ? ("sku_management.messages.bulk_apply_confirmation_skus_plural"
                  | translate)
                : ("sku_management.messages.bulk_apply_confirmation_skus"
                  | translate)
            }}?
          }
        </p>
        <p class="text-sm text-gray-500">
          {{
            "sku_management.messages.bulk_apply_confirmation_warning"
              | translate
          }}
        </p>
      </div>
      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
          <app-button
            [label]="'common.actions.cancel' | translate"
            type="secondary"
            [outlined]="true"
            size="small"
            (emitOnClick)="cancelBulkApply()"
          ></app-button>
          <app-button
            [label]="'sku_management.create.apply_to_selected' | translate"
            type="primary"
            size="small"
            (emitOnClick)="confirmBulkApply()"
          ></app-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Package Component Selection Dialog -->
    <p-dialog
      [visible]="showPackageComponentSelector()"
      (visibleChange)="showPackageComponentSelector.set($event)"
      [modal]="true"
      [style]="{ width: '90vw', maxWidth: '1200px' }"
      [draggable]="false"
      [resizable]="false"
      [closable]="false"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center justify-between w-full">
          <h2 class="text-xl font-semibold text-gray-800 m-0">
            {{
              "sku_management.create.package_components.select_child_sku"
                | translate
            }}
          </h2>
          <button
            type="button"
            class="custom-close-btn"
            (click)="closePackageComponentSelector()"
            [attr.aria-label]="'common.actions.close_dialog' | translate"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
      </ng-template>

      <div class="product-selection-dialog-content">
        <div class="bg-gray-50 p-4 rounded-lg flex-shrink-0">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="space-y-2">
              <label
                for="package-component-search-code"
                class="text-xs font-medium text-gray-700"
              >
                {{
                  "sku_management.create.package_components.sku_code"
                    | translate
                }}
              </label>
              <input
                pInputText
                id="package-component-search-code"
                [(ngModel)]="packageComponentSearchForm.skuCode"
                [placeholder]="
                  'sku_management.create.enter_sku_code' | translate
                "
                class="w-full"
              />
            </div>
            <div class="space-y-2">
              <label
                for="package-component-search-name"
                class="text-xs font-medium text-gray-700"
              >
                {{
                  "sku_management.create.package_components.sku_name"
                    | translate
                }}
              </label>
              <input
                pInputText
                id="package-component-search-name"
                [(ngModel)]="packageComponentSearchForm.skuName"
                [placeholder]="
                  'sku_management.create.enter_sku_name' | translate
                "
                class="w-full"
              />
            </div>
          </div>
          <div class="flex gap-2 mt-4 justify-end">
            <app-button
              [label]="'common.actions.search' | translate"
              type="info"
              size="small"
              (emitOnClick)="onPackageComponentSearch()"
            ></app-button>
            <app-button
              [label]="'common.actions.reset' | translate"
              type="secondary"
              [outlined]="true"
              size="small"
              (emitOnClick)="onPackageComponentSearchReset()"
            ></app-button>
          </div>
        </div>

        <div class="product-table-wrapper flex-1">
          <app-data-table
            [dataSource]="packageComponentOptions()"
            [columns]="packageComponentTableColumns()"
            [loading]="packageComponentLoading()"
            [hasCheckColumn]="true"
            [hasSerialColumn]="false"
            [hasHeaderCheckbox]="true"
            [rows]="packageComponentPageSize()"
            [first]="
              (packageComponentCurrentPage() - 1) *
              packageComponentPageSize()
            "
            [totalRecords]="packageComponentTotalRecords()"
            [scrollHeight]="'calc(90vh - 400px)'"
            [selection]="selectedPackageComponentsForTable()"
            (selectionChange)="onPackageComponentSelectionChange($event)"
            (pageChange)="onPackageComponentPageChange($event)"
          >
          </app-data-table>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-end gap-2">
          <app-button
            [label]="'common.actions.cancel' | translate"
            type="secondary"
            [outlined]="true"
            size="small"
            (emitOnClick)="closePackageComponentSelector()"
          ></app-button>
          <app-button
            [label]="'common.actions.confirm' | translate"
            type="info"
            size="small"
            (emitOnClick)="onPackageComponentSelectionConfirm()"
            [disabled]="
              selectedPackageComponentsForTable().length === 0 ||
              packageComponentLoading()
            "
          ></app-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Barcode Dialog -->
    <app-barcode-dialog
      [(visible)]="showBarcodeDialog"
      [initialData]="editingBarcode()?.barcode || null"
      (confirm)="onBarcodeDialogConfirm($event)"
    ></app-barcode-dialog>
  </div>
</div>
